import React, { useState, useEffect } from 'react';
import MarketPerformancePanels from '../components/MarketPerformancePanels';
import {
  Box,
  Typography,
  Tab,
  Tabs,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import MarketTreemap, { ViewMode } from '../components/MarketTreemap';

// Supported market types
export type MarketType = 'sp500' | 'nasdaq100' | 'nasdaq_composite' | 'russell2000' | 'ftse100' | 'ftse250' | 'cac40' | 'dax' | 'ibex35' | 'aex' | 'bel20' | 'nikkei225' | 'kospi' | 'smi' | 'spi' | 'ftse_mib' | 'sti';

// Market configuration - grouped by region
export const MARKET_CONFIG: Record<MarketType, { name: string; country: string; flag: string; currency: string }> = {
  sp500:            { name: 'S&P 500',          country: 'US', flag: '🇺🇸', currency: 'USD' },
  nasdaq100:        { name: 'NASDAQ 100',       country: 'US', flag: '🇺🇸', currency: 'USD' },
  nasdaq_composite: { name: 'NASDAQ Comp.',     country: 'US', flag: '🇺🇸', currency: 'USD' },
  russell2000:      { name: 'Russell 2000',     country: 'US', flag: '🇺🇸', currency: 'USD' },
  ftse100:          { name: 'FTSE 100',         country: 'UK', flag: '🇬🇧', currency: 'GBP' },
  ftse250:          { name: 'FTSE 250',         country: 'UK', flag: '🇬🇧', currency: 'GBP' },
  cac40:            { name: 'CAC 40',           country: 'FR', flag: '🇫🇷', currency: 'EUR' },
  dax:              { name: 'DAX',              country: 'DE', flag: '🇩🇪', currency: 'EUR' },
  ibex35:           { name: 'IBEX 35',          country: 'ES', flag: '🇪🇸', currency: 'EUR' },
  aex:              { name: 'AEX',              country: 'NL', flag: '🇳🇱', currency: 'EUR' },
  bel20:            { name: 'BEL 20',           country: 'BE', flag: '🇧🇪', currency: 'EUR' },
  nikkei225:        { name: 'Nikkei 225',       country: 'JP', flag: '🇯🇵', currency: 'JPY' },
  kospi:            { name: 'KOSPI',            country: 'KR', flag: '🇰🇷', currency: 'KRW' },
  smi:              { name: 'SMI',              country: 'CH', flag: '🇨🇭', currency: 'CHF' },
  spi:              { name: 'SPI',              country: 'CH', flag: '🇨🇭', currency: 'CHF' },
  ftse_mib:         { name: 'FTSE MIB',        country: 'IT', flag: '🇮🇹', currency: 'EUR' },
  sti:              { name: 'STI',              country: 'SG', flag: '🇸🇬', currency: 'SGD' },
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`market-tabpanel-${index}`}
      aria-labelledby={`market-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const MarketMap: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [marketType, setMarketType] = useState<MarketType>('sp500');

  const handleMarketChange = (_: React.MouseEvent<HTMLElement>, newMarket: MarketType | null) => {
    if (newMarket) {
      setMarketType(newMarket);
      setActiveTab(0);
    }
  };

  const currentMarketLabel = MARKET_CONFIG[marketType].name;

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Market Maps
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Visualize {currentMarketLabel} market performance across sectors, industries, and individual stocks
          </Typography>
        </Box>

        {/* Market Selector — map tabs only. The Performance panel compares
            across markets, so picking one here would mean nothing. */}
        {activeTab !== 2 && (
        <ToggleButtonGroup
          value={marketType}
          exclusive
          onChange={handleMarketChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              px: 1.5,
              py: 0.5,
              fontSize: '0.8rem',
            },
            flexWrap: 'wrap',
            gap: 0.5,
          }}
        >
          {(Object.keys(MARKET_CONFIG) as MarketType[]).map((market) => (
            <ToggleButton key={market} value={market}>
              {MARKET_CONFIG[market].flag} {MARKET_CONFIG[market].name}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        )}
      </Box>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2, mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label={`${currentMarketLabel} Map`} />
          <Tab label="Industry Map" />
          {/* Cross-market sector/industry performance, moved here from the old
              Macro page — it belongs with the sector visualisations, not with
              the economic series. Its own Sectors/Industry sub-tab lives inside
              the panel because both halves share the timeframe control and the
              country summary cards. */}
          <Tab label="Performance" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <MarketTreemap marketType={marketType} />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <MarketTreemap marketType={marketType} viewMode="industry" />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <MarketPerformancePanels />
      </TabPanel>

    </Box>
  );
};

export default MarketMap;
